import { supabase } from '../supabase'
import type {
  AdminUser,
  Campaign,
  CampaignKind,
  Category,
  Perms,
  Portion,
  Product,
  NetworkPrinter,
  ReceiptSettings,
  Role,
  Table,
  Hall,
  Material,
  MaterialUnit,
  ProductCost,
  RecipeLine,
  Tare,
  LoyaltySettings,
} from '../../types'

/**
 * The menu, read from Supabase.
 *
 * Each subscription follows the same three steps: hand over what is cached,
 * open a live channel, then fetch. The order matters, opening the channel
 * before the fetch means a change landing between the two is not missed.
 *
 * Postgres columns are snake_case and the app's types are camelCase, so every
 * row passes through a mapper. Doing it in one place is what keeps the rest of
 * the app from having to know which database it is talking to.
 */

type Row = Record<string, unknown>

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback)
const num = (v: unknown, fallback = 0): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback
const bool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback

/** null means "inherit from the category"; an array is a decision. */
const notes = (v: unknown): string[] | null =>
  Array.isArray(v) ? (v as string[]) : null

function toCategory(r: Row): Category {
  return {
    id: str(r.id),
    name: str(r.name),
    sortOrder: num(r.sort_order),
    active: bool(r.active, true),
    station: str(r.station) || undefined,
    noteOptions: notes(r.note_options),
    color: str(r.color) || undefined,
  }
}

function toProduct(r: Row): Product {
  return {
    id: str(r.id),
    name: str(r.name),
    categoryId: str(r.category_id),
    // bigint arrives as a number for values this size, but a string is
    // possible; Number() covers both without losing the integer.
    priceKurus: Number(r.price_kurus ?? 0),
    description: str(r.description) || null,
    active: bool(r.active, true),
    soldOut: bool(r.sold_out, false),
    sortOrder: num(r.sort_order),
    noteOptions: notes(r.note_options),
    byWeight: bool(r.by_weight, false),
    tare: toTare(r.tare),
    imageUrl: str(r.image_url) || null,
    stock: r.stock === null || r.stock === undefined ? null : Number(r.stock),
    stockMin: r.stock_min === null || r.stock_min === undefined ? null : Number(r.stock_min),
    barcodes: strList(r.barcodes),
    station: str(r.station) || null,
    portions: toPortions(r.portions),
  }
}

/** [{name, priceKurus}] → Portion[]; bozuk ya da boşsa null. */
function toPortions(v: unknown): Portion[] | null {
  if (!Array.isArray(v)) return null
  const list = v
    .map((x) => ({ name: str((x as Row)?.name).trim(), priceKurus: Number((x as Row)?.priceKurus) }))
    .filter((x) => x.name && Number.isFinite(x.priceKurus) && x.priceKurus >= 0)
  return list.length ? list : null
}

/** {"name": "...", "gr": 280} → Tare; bozuk ya da boşsa null. */
function toTare(v: unknown): Tare | null {
  if (!v || typeof v !== 'object') return null
  const t = v as { name?: unknown; gr?: unknown }
  const gr = Number(t.gr)
  return typeof t.name === 'string' && t.name.trim() && Number.isFinite(gr) && gr >= 0
    ? { name: t.name.trim(), gr: Math.round(gr) }
    : null
}

function toTable(r: Row): Table {
  return {
    id: str(r.id),
    name: str(r.name),
    sortOrder: num(r.sort_order),
    active: bool(r.active, true),
    hallId: str(r.hall_id) || undefined,
    qrToken: str(r.qr_token) || undefined,
  }
}

const optNum = (v: unknown): number | undefined =>
  v === null || v === undefined || v === '' ? undefined : Number(v)
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])

function toCampaign(r: Row): Campaign {
  return {
    id: str(r.id),
    name: str(r.name),
    kind: (['percent', 'amount', 'bogo'].includes(str(r.kind)) ? str(r.kind) : 'percent') as CampaignKind,
    percent: optNum(r.percent),
    amountKurus: optNum(r.amount_kurus),
    minTotalKurus: optNum(r.min_total_kurus),
    buyQty: optNum(r.buy_qty),
    payQty: optNum(r.pay_qty),
    categoryIds: strList(r.category_ids),
    productIds: strList(r.product_ids),
    days: Array.isArray(r.days) ? r.days.map(Number) : [],
    startTime: str(r.start_time) || undefined,
    endTime: str(r.end_time) || undefined,
    startsOn: str(r.starts_on) || undefined,
    endsOn: str(r.ends_on) || undefined,
    active: r.active !== false,
    sortOrder: num(r.sort_order),
    showOnQr: r.show_on_qr === true,
    imageUrl: str(r.image_url) || null,
  }
}

function toHall(r: Row): Hall {
  return {
    id: str(r.id),
    name: str(r.name),
    sortOrder: num(r.sort_order),
  }
}

/**
 * Watches one table for one institution.
 *
 * Unfiltered by design, exactly as the Firestore listeners were: a menu is
 * twenty to eighty rows, and holding all of it means the whole catalogue is
 * in hand before the signal drops. Sorting and active-filtering happen in
 * memory.
 */
function watch<T>(
  table: string,
  tenantId: string,
  map: (r: Row) => T,
  sort: (a: T, b: T) => number,
  onData: (items: T[]) => void,
  onError: (e: Error) => void,
): () => void {
  if (!supabase) {
    onError(new Error('Supabase yapılandırılmamış'))
    return () => {}
  }

  let cancelled = false

  const read = async () => {
    const { data, error } = await supabase!
      .from(table)
      .select('*')
      .eq('tenant_id', tenantId)

    if (cancelled) return
    if (error) {
      onError(new Error(error.message))
      return
    }
    onData((data ?? []).map(map).sort(sort))
  }

  // The channel opens first so that a change arriving during the initial
  // fetch still reaches us; the fetch that follows is what fills the screen.
  //
  // The name has to be unique per subscription, not per table. Supabase keeps
  // one channel object per name and refuses to attach a listener to one that
  // has already subscribed, so a second mount of the same component (which
  // React does in development, on purpose) threw and took the screen down
  // with it. Two tills watching the same table is the same situation.
  const channel = supabase
    .channel(`${table}:${tenantId}:${crypto.randomUUID()}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter: `tenant_id=eq.${tenantId}` },
      () => void read(),
    )
    .subscribe((status) => {
      // Re-reading on every (re)subscribe is what recovers the changes missed
      // while the connection was down, there is no cursor to resume from.
      if (status === 'SUBSCRIBED') void read()
    })

  return () => {
    cancelled = true
    void supabase!.removeChannel(channel)
  }
}

const bySort = <T extends { sortOrder: number }>(a: T, b: T) => a.sortOrder - b.sortOrder

export function subscribeCategories(
  tenantId: string,
  onData: (categories: Category[]) => void,
  onError: (error: Error) => void,
): () => void {
  return watch('categories', tenantId, toCategory, bySort, onData, onError)
}

export function subscribeProducts(
  tenantId: string,
  onData: (products: Product[]) => void,
  onError: (error: Error) => void,
): () => void {
  return watch('products', tenantId, toProduct, bySort, onData, onError)
}

export function subscribeTables(
  tenantId: string,
  onData: (tables: Table[]) => void,
  onError: (error: Error) => void,
): () => void {
  return watch('pos_tables', tenantId, toTable, bySort, onData, onError)
}

export function subscribeHalls(
  tenantId: string,
  onData: (halls: Hall[]) => void,
  onError: (error: Error) => void,
): () => void {
  return watch('halls', tenantId, toHall, bySort, onData, onError)
}

const ROLES: Role[] = ['owner', 'manager', 'cashier', 'waiter', 'kitchen']

export function subscribeStaff(
  tenantId: string,
  onData: (staff: AdminUser[]) => void,
  onError: (error: Error) => void,
): () => void {
  return watch(
    'admins',
    tenantId,
    (r): AdminUser => ({
      uid: str(r.id),
      email: str(r.email),
      username: str(r.username) || str(r.email).split('@')[0],
      displayName: str(r.display_name) || undefined,
      // Anything unrecognised falls back to the least-privileged role rather
      // than the most: a bad value must never read as staff who can take money.
      role: ROLES.includes(str(r.role) as Role) ? (str(r.role) as Role) : 'waiter',
      // Same principle: anything that is not an object reads as no
      // permissions, never as all of them.
      perms:
        r.perms && typeof r.perms === 'object' && !Array.isArray(r.perms)
          ? (r.perms as Perms)
          : {},
    }),
    (a, b) => a.username.localeCompare(b.username, 'tr'),
    onData,
    onError,
  )
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Every write goes through here, so a missing client fails the same way. */
function client() {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  return supabase
}

function fail(error: { message: string } | null) {
  if (error) throw new Error(error.message)
}

export async function saveProduct(
  tenantId: string,
  product: Omit<Product, 'id'> & { id?: string },
): Promise<string> {
  const id = product.id ?? crypto.randomUUID()
  const { error } = await client()
    .from('products')
    .upsert({
      id,
      tenant_id: tenantId,
      category_id: product.categoryId,
      name: product.name,
      price_kurus: product.priceKurus,
      description: product.description ?? null,
      active: product.active,
      // Yalnız verildiyse: ürün penceresinden kaydetmek Tükendi'yi (stok
      // bitince kendiliğinden konanı da) sıfırlamasın. Yeni üründe varsayılan false.
      ...('soldOut' in product ? { sold_out: product.soldOut ?? false } : {}),
      sort_order: product.sortOrder,
      note_options: product.noteOptions ?? null,
      by_weight: product.byWeight ?? false,
      tare: product.byWeight && product.tare ? product.tare : null,
      // Yalnız verildiyse: fotoğrafı bilmeyen kayıt (CSV içe aktarma)
      // mevcut fotoğrafı silmesin.
      ...('imageUrl' in product ? { image_url: product.imageUrl ?? null } : {}),
      // Stok yalnız patron değiştirdiyse yazılıyor: pencere açıkken yapılan
      // satışları eski sayıyla ezmesin.
      ...('stock' in product ? { stock: product.stock ?? null } : {}),
      ...('stockMin' in product ? { stock_min: product.stockMin ?? null } : {}),
      // Yalnız verildiyse: CSV içe aktarma barkodları silmesin.
      ...('barcodes' in product ? { barcodes: product.barcodes ?? [] } : {}),
      // Yalnız verildiyse: CSV içe aktarma ürüne özel tezgahı silmesin.
      ...('station' in product ? { station: product.station || null } : {}),
      // Yalnız verildiyse: CSV içe aktarma porsiyonları silmesin.
      ...('portions' in product ? { portions: product.portions?.length ? product.portions : null } : {}),
      updated_at: new Date().toISOString(),
    })
  fail(error)
  return id
}

/**
 * Shows or hides an item on the till, used when stock runs out mid-service
 * and again when it is restocked. Never a hard delete: that would orphan the
 * lines of every order that ever sold it.
 */
export async function setProductActive(
  tenantId: string,
  productId: string,
  active: boolean,
): Promise<void> {
  const { error } = await client()
    .from('products')
    .update({ active, updated_at: new Date().toISOString() })
    .eq('id', productId)
    .eq('tenant_id', tenantId)
  fail(error)
}

export async function setProductSoldOut(
  tenantId: string,
  productId: string,
  soldOut: boolean,
): Promise<void> {
  const { error } = await client()
    .from('products')
    .update({ sold_out: soldOut, updated_at: new Date().toISOString() })
    .eq('id', productId)
    .eq('tenant_id', tenantId)
  fail(error)
}

export async function deleteProduct(tenantId: string, productId: string): Promise<void> {
  const { error } = await client()
    .from('products')
    .delete()
    .eq('id', productId)
    .eq('tenant_id', tenantId)
  fail(error)
}

export async function saveCategory(
  tenantId: string,
  category: Omit<Category, 'id'> & { id?: string },
): Promise<string> {
  const id = category.id ?? crypto.randomUUID()
  const { error } = await client()
    .from('categories')
    .upsert({
      id,
      tenant_id: tenantId,
      name: category.name,
      sort_order: category.sortOrder,
      active: category.active,
      station: category.station ?? null,
      note_options: category.noteOptions ?? null,
      // Yalnız renk seçildiyse: sütun (supabase/kategori-rengi.sql) canlıya
      // eklenmeden önce de kategori kaydı bozulmasın.
      ...(category.color !== undefined ? { color: category.color } : {}),
      updated_at: new Date().toISOString(),
    })
  fail(error)
  return id
}

export async function deleteCategory(tenantId: string, categoryId: string): Promise<void> {
  const { error } = await client()
    .from('categories')
    .delete()
    .eq('id', categoryId)
    .eq('tenant_id', tenantId)
  fail(error)
}

/**
 * Swaps two rows' positions.
 *
 * Two writes rather than one transaction. Ordering is cosmetic, the till
 * lists things in whatever order it finds, so a half-applied swap is a
 * cosmetic problem, not a lost sale, and is fixed by swapping again.
 */
async function swapOrder(
  table: 'categories' | 'products',
  tenantId: string,
  a: { id: string; sortOrder: number },
  b: { id: string; sortOrder: number },
): Promise<void> {
  const db = client()
  const [x, y] = await Promise.all([
    db.from(table).update({ sort_order: b.sortOrder }).eq('id', a.id).eq('tenant_id', tenantId),
    db.from(table).update({ sort_order: a.sortOrder }).eq('id', b.id).eq('tenant_id', tenantId),
  ])
  fail(x.error)
  fail(y.error)
}

export async function swapCategoryOrder(
  tenantId: string,
  a: Category,
  b: Category,
): Promise<void> {
  await swapOrder('categories', tenantId, a, b)
}

export async function swapProductOrder(
  tenantId: string,
  a: Product,
  b: Product,
): Promise<void> {
  await swapOrder('products', tenantId, a, b)
}

/**
 * Sürükle-bırak sırası (25 Eki 2026): listenin yeni hali, baştan sona.
 * Yalnız yeri değişenler yazılıyor; sıra numarası liste içindeki yeri.
 */
async function reorder(
  table: 'categories' | 'products',
  tenantId: string,
  items: { id: string; sortOrder: number }[],
): Promise<void> {
  const db = client()
  const writes = items
    .map((it, i) => ({ it, i }))
    .filter(({ it, i }) => it.sortOrder !== i)
    .map(({ it, i }) => db.from(table).update({ sort_order: i }).eq('id', it.id).eq('tenant_id', tenantId))
  for (const r of await Promise.all(writes)) fail(r.error)
}

export async function reorderCategories(tenantId: string, items: Category[]): Promise<void> {
  await reorder('categories', tenantId, items)
}

/**
 * Ürünlerin yeni yerleşimi: başka kategoriye geçen ürünün kategorisi ve
 * sırası, yeri değişenlerin sırası. Yalnız değişen alanlar yazılıyor.
 */
/**
 * Ürüne özel bir tezgahı siler (25 Eki 2026): o tezgaha yazılmış ürünler
 * kategorilerinin tezgahına döner. Kategorinin tezgahı burada silinmiyor.
 */
export async function clearProductStation(tenantId: string, station: string): Promise<void> {
  const { error } = await client()
    .from('products')
    .update({ station: null })
    .eq('tenant_id', tenantId)
    .eq('station', station)
  fail(error)
}

export async function moveProducts(
  tenantId: string,
  moves: { product: Product; categoryId: string; sortOrder: number }[],
): Promise<void> {
  const db = client()
  const writes = moves
    .filter((m) => m.product.categoryId !== m.categoryId || m.product.sortOrder !== m.sortOrder)
    .map((m) =>
      db
        .from('products')
        .update({ category_id: m.categoryId, sort_order: m.sortOrder })
        .eq('id', m.product.id)
        .eq('tenant_id', tenantId),
    )
  for (const r of await Promise.all(writes)) fail(r.error)
}

export async function saveTable(
  tenantId: string,
  table: Omit<Table, 'id'> & { id?: string },
): Promise<string> {
  const id = table.id ?? crypto.randomUUID()
  const { error } = await client().from('pos_tables').upsert({
    id,
    tenant_id: tenantId,
    name: table.name,
    sort_order: table.sortOrder,
    active: table.active,
    hall_id: table.hallId ?? null,
  })
  fail(error)
  return id
}

export async function saveHall(
  tenantId: string,
  hall: Omit<Hall, 'id'> & { id?: string },
): Promise<string> {
  const id = hall.id ?? crypto.randomUUID()
  const { error } = await client().from('halls').upsert({
    id,
    tenant_id: tenantId,
    name: hall.name,
    sort_order: hall.sortOrder,
  })
  fail(error)
  return id
}

/**
 * Removes a section. Its tables are not removed with it, the foreign key is
 * ON DELETE SET NULL, so they fall back to standing on their own. Closing off
 * a part of the room is not the same as throwing away the tables in it.
 */
export async function deleteHall(tenantId: string, hallId: string): Promise<void> {
  const { error } = await client()
    .from('halls')
    .delete()
    .eq('id', hallId)
    .eq('tenant_id', tenantId)
  fail(error)
}

/** Kampanyalar: kasa öneri için, patron düzenlemek için dinliyor. */
export function subscribeCampaigns(
  tenantId: string,
  onData: (campaigns: Campaign[]) => void,
  onError: (error: Error) => void,
): () => void {
  return watch('campaigns', tenantId, toCampaign, bySort, onData, onError)
}

export async function saveCampaign(
  tenantId: string,
  c: Omit<Campaign, 'id'> & { id?: string },
): Promise<string> {
  const id = c.id ?? crypto.randomUUID()
  const { error } = await client().from('campaigns').upsert({
    id,
    tenant_id: tenantId,
    name: c.name,
    kind: c.kind,
    percent: c.kind === 'percent' ? (c.percent ?? null) : null,
    amount_kurus: c.kind === 'amount' ? (c.amountKurus ?? null) : null,
    min_total_kurus: c.kind === 'bogo' ? null : (c.minTotalKurus ?? null),
    buy_qty: c.kind === 'bogo' ? (c.buyQty ?? null) : null,
    pay_qty: c.kind === 'bogo' ? (c.payQty ?? null) : null,
    category_ids: c.categoryIds,
    product_ids: c.productIds,
    days: c.days,
    start_time: c.startTime || null,
    end_time: c.endTime || null,
    starts_on: c.startsOn || null,
    ends_on: c.endsOn || null,
    active: c.active,
    sort_order: c.sortOrder,
    show_on_qr: c.showOnQr,
    ...('imageUrl' in c ? { image_url: c.imageUrl ?? null } : {}),
  })
  fail(error)
  return id
}

export async function deleteCampaign(tenantId: string, campaignId: string): Promise<void> {
  const { error } = await client()
    .from('campaigns')
    .delete()
    .eq('id', campaignId)
    .eq('tenant_id', tenantId)
  fail(error)
}

export async function deleteTable(tenantId: string, tableId: string): Promise<void> {
  const { error } = await client()
    .from('pos_tables')
    .delete()
    .eq('id', tableId)
    .eq('tenant_id', tenantId)
  fail(error)
}

/** Only the settings an owner edits; the rest of the row is fixed. */
export async function saveTenantSettings(
  tenantId: string,
  settings: Partial<{
    studentDiscountPercent: number
    discounts: number[]
    displayName: string
    roleLabels: Record<string, string>
    receipt: ReceiptSettings
    cashReceipt: ReceiptSettings
    printers: NetworkPrinter[]
    usePrinter: boolean
    loyalty: LoyaltySettings
    kitchenBell: string
  }>,
): Promise<void> {
  const row: Record<string, unknown> = {}
  if (settings.studentDiscountPercent !== undefined) {
    row.student_discount_percent = settings.studentDiscountPercent
  }
  if (settings.discounts !== undefined) row.discounts = settings.discounts
  if (settings.displayName !== undefined) row.display_name = settings.displayName
  if (settings.roleLabels !== undefined) row.role_labels = settings.roleLabels
  if (settings.receipt !== undefined) row.receipt = settings.receipt
  if (settings.cashReceipt !== undefined) row.cash_receipt = settings.cashReceipt
  if (settings.printers !== undefined) row.printers = settings.printers
  if (settings.usePrinter !== undefined) row.use_printer = settings.usePrinter
  if (settings.loyalty !== undefined) row.loyalty = settings.loyalty
  if (settings.kitchenBell !== undefined) row.kitchen_bell = settings.kitchenBell

  if (Object.keys(row).length === 0) return

  const { error } = await client().from('tenants').update(row).eq('id', tenantId)
  fail(error)
}

export async function saveStaffDisplayName(
  tenantId: string,
  uid: string,
  displayName: string,
): Promise<void> {
  const { error } = await client()
    .from('admins')
    .update({ display_name: displayName })
    .eq('id', uid)
    .eq('tenant_id', tenantId)
  fail(error)
}

/**
 * Writes one person's permissions.
 *
 * The whole object rather than one key, because that is what the dialog holds
 *, and RLS lets only an owner through, so a waiter calling this on their own
 * row is refused by the database rather than by the screen.
 */
export async function saveStaffPerms(
  tenantId: string,
  uid: string,
  perms: Perms,
): Promise<void> {
  const { error } = await client()
    .from('admins')
    .update({ perms })
    .eq('id', uid)
    .eq('tenant_id', tenantId)
  fail(error)
}

// ============================================================
// Maliyet (18 Eki 2026): hammaddeler ve ürün maliyetleri. İki tabloyu da
// yalnız patron okuyabiliyor (supabase/maliyet.sql); başkası boş liste alır.
// ============================================================

const UNITS: MaterialUnit[] = ['kg', 'adet', 'lt']

function toMaterial(r: Row): Material {
  const unit = str(r.unit) as MaterialUnit
  return {
    id: str(r.id),
    name: str(r.name),
    unit: UNITS.includes(unit) ? unit : 'kg',
    priceKurus: Number(r.price_kurus ?? 0),
  }
}

function toRecipe(v: unknown): RecipeLine[] {
  if (!Array.isArray(v)) return []
  return v
    .map((x) => x as Record<string, unknown>)
    .filter((x) => typeof x.materialId === 'string' && typeof x.qty === 'number' && Number.isFinite(x.qty))
    .map((x) => ({ materialId: x.materialId as string, qty: x.qty as number }))
}

function toProductCost(r: Row): ProductCost {
  return {
    productId: str(r.product_id),
    mode: r.mode === 'recipe' ? 'recipe' : 'direct',
    costKurus: r.cost_kurus == null ? null : Number(r.cost_kurus),
    recipe: toRecipe(r.recipe),
  }
}

const byName = (a: Material, b: Material) => a.name.localeCompare(b.name, 'tr')

export function subscribeMaterials(
  tenantId: string,
  onData: (materials: Material[]) => void,
  onError: (error: Error) => void,
): () => void {
  return watch('materials', tenantId, toMaterial, byName, onData, onError)
}

export function subscribeProductCosts(
  tenantId: string,
  onData: (costs: ProductCost[]) => void,
  onError: (error: Error) => void,
): () => void {
  return watch('product_costs', tenantId, toProductCost, () => 0, onData, onError)
}

export async function saveMaterial(
  tenantId: string,
  material: Omit<Material, 'id'> & { id?: string },
): Promise<string> {
  const id = material.id ?? crypto.randomUUID()
  const { error } = await client().from('materials').upsert({
    id,
    tenant_id: tenantId,
    name: material.name,
    unit: material.unit,
    price_kurus: material.priceKurus,
    updated_at: new Date().toISOString(),
  })
  fail(error)
  return id
}

/** Hammaddeyi siler; onu kullanan reçetelerde satır kalır ama hesaba girmez. */
export async function deleteMaterial(tenantId: string, materialId: string): Promise<void> {
  const { error } = await client()
    .from('materials')
    .delete()
    .eq('id', materialId)
    .eq('tenant_id', tenantId)
  fail(error)
}

export async function saveProductCost(tenantId: string, cost: ProductCost): Promise<void> {
  const { error } = await client().from('product_costs').upsert({
    product_id: cost.productId,
    tenant_id: tenantId,
    mode: cost.mode,
    cost_kurus: cost.mode === 'direct' ? cost.costKurus : null,
    recipe: cost.mode === 'recipe' ? cost.recipe : [],
    updated_at: new Date().toISOString(),
  })
  fail(error)
}

export async function deleteProductCost(tenantId: string, productId: string): Promise<void> {
  const { error } = await client()
    .from('product_costs')
    .delete()
    .eq('product_id', productId)
    .eq('tenant_id', tenantId)
  fail(error)
}
